import React, { useEffect, useState } from 'react';
import { 
  ShareIcon, 
  ClipboardDocumentIcon,
  ChartBarIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  CheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface ReferralStats {
  code: string;
  totalClicks: number;
  conversions: number;
  conversionRate: number;
  totalEarnings: number;
  pendingEarnings: number;
  currentTier: {
    name: string;
    reward: number;
  };
  nextTier?: {
    name: string;
    reward: number;
    conversionsNeeded: number;
  };
}

const ReferralDashboard: React.FC = () => {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/referrals/stats');
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching referral stats:', err);
      setError(err.response?.data?.error || 'Failed to load referral stats');
    } finally {
      setLoading(false);
    }
  };

  const getReferralUrl = () => {
    if (!stats) return '';
    return `${window.location.origin}/signup?ref=${stats.code}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getReferralUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOnTwitter = () => {
    const url = getReferralUrl();
    const text = encodeURIComponent('Join me on Patreon! Use my referral link to get started.');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnFacebook = () => {
    const url = getReferralUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareViaEmail = () => {
    const url = getReferralUrl();
    const subject = encodeURIComponent('Join me on Patreon!');
    const body = encodeURIComponent(`I thought you might be interested in joining Patreon. Use my referral link: ${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'No referral data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <UserGroupIcon className="h-8 w-8 text-blue-600" />
          Referral Program
        </h1>
        <p className="mt-2 text-gray-600">
          Earn rewards by inviting friends to join Patreon
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Clicks</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalClicks}</p>
            </div>
            <ChartBarIcon className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Conversions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.conversions}</p>
            </div>
            <UserGroupIcon className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats.conversionRate * 100).toFixed(1)}%
              </p>
            </div>
            <TrophyIcon className="h-10 w-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">
                ${stats.totalEarnings.toFixed(2)}
              </p>
            </div>
            <CurrencyDollarIcon className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Referral Link</h2>
        
        <div className="flex items-center gap-3 mb-4">
          <input
            type="text"
            value={getReferralUrl()}
            readOnly
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
          />
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {copied ? (
              <>
                <CheckIcon className="h-5 w-5" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-5 w-5" />
                Copy
              </>
            )}
          </button>
        </div>

        {/* Share Buttons */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">Share via:</span>
          <button
            onClick={shareOnTwitter}
            className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm"
          >
            Twitter
          </button>
          <button
            onClick={shareOnFacebook}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            Facebook
          </button>
          <button
            onClick={shareViaEmail}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            Email
          </button>
        </div>
      </div>

      {/* Tier Progress */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Reward Tier Progress</h2>
        
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-lg font-semibold text-gray-900">
                Current: {stats.currentTier.name}
              </span>
              <span className="ml-3 text-gray-600">
                ${stats.currentTier.reward} per conversion
              </span>
            </div>
            {stats.nextTier && (
              <div className="text-right">
                <span className="text-sm text-gray-600">Next tier:</span>
                <span className="ml-2 text-lg font-semibold text-purple-600">
                  {stats.nextTier.name}
                </span>
              </div>
            )}
          </div>

          {stats.nextTier && (
            <>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, (stats.conversions / (stats.conversions + stats.nextTier.conversionsNeeded)) * 100)}%`
                  }}
                />
              </div>
              <p className="text-sm text-gray-600">
                {stats.nextTier.conversionsNeeded} more conversions to reach {stats.nextTier.name} tier
                (${stats.nextTier.reward} per conversion)
              </p>
            </>
          )}
        </div>

        {stats.pendingEarnings > 0 && (
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Earnings</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${stats.pendingEarnings.toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Will be paid within 30 days</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <ShareIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">1. Share Your Link</h3>
            <p className="text-sm text-gray-600">
              Share your unique referral link with friends, family, or on social media
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <UserGroupIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">2. They Sign Up</h3>
            <p className="text-sm text-gray-600">
              When someone signs up using your link and becomes a patron
            </p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">3. Earn Rewards</h3>
            <p className="text-sm text-gray-600">
              You earn rewards for each successful conversion. More referrals = higher tiers!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboard;
